import { AxiosError } from 'axios';
import { useGlobalContext } from 'GlobalContext';
import moment from 'moment';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { PatchOrderRequestData } from 'services/interfaces/order';
import { Mixpanel } from 'services/mixpanel';
import { getDanfseNfse, getXmlNfse, sendEmailNfse } from 'services/nfse';
import {
  cancel,
  generateInvoice,
  load,
  patch,
  payWithCard,
  reversalOrder,
} from 'services/order';
import { load as loadTransactions } from 'services/transaction';
import { Order } from 'shared/models/order';
import { CreditCardTransaction } from 'shared/models/transaction';
import OrderModal from './OrderModal';

interface OrderModalProps {
  isOpen: boolean;
  onClose(): void;
  token?: string;
}

interface OrderModalContextData {
  loading: boolean;
  setLoading(b: boolean): void;
  loadOrder(): void;
  order?: Order;
  editing: boolean;
  startEditing(): void;
  abortEditing(): void;
  transactions: CreditCardTransaction[];
  inputDueDate: string;
  setInputDueDate(s: string): void;
  inputPrice: string;
  setInputPriceState(n: number | string): void;
  inputMethod: 1 | 2;
  setInputMethod(n: 1 | 2): void;
  submitForm(): void;
  processPayment(): void;
  cancelOrder(): void;
  generateOrderInvoice(): void;
  downloadDanfse(): void;
  downloadXml(): void;
  sendEmail(): void;
  reverseOrder(): void;
}

const Context = createContext<OrderModalContextData>(
  {} as OrderModalContextData,
);

export const useOrderModalContext = () => useContext(Context);

const AddServicesContext: React.FC<OrderModalProps> = ({
  token,
  isOpen,
  onClose: onCloseProp,
}) => {
  const { confirm } = useGlobalContext();
  const { loadCustomer } = useCustomerContext();

  const [order, setOrder] = useState<Order>();
  const [transactions, setTransactions] = useState<CreditCardTransaction[]>([]);

  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);

  const [inputDueDate, setInputDueDate] = useState<string>(
    moment().format('YYYY-MM-DD'),
  );
  const [inputPrice, setInputPrice] = useState<string>('');
  const [inputMethod, setInputMethod] = useState<1 | 2>(1);

  const onClose = useCallback(() => {
    setLoading(false);
    setEditing(false);

    setOrder(undefined);
    onCloseProp();
  }, [onCloseProp]);

  const loadOrder = useCallback(() => {
    if (!token) return;
    setLoading(true);
    load(token)
      .then((response) => {
        setOrder(new Order(response.data));

        if (response?.data?.bank_id === 1 && response?.data?.id) {
          loadTransactions(response.data.id)
            .then((res) =>
              setTransactions(
                res.data.map((data) => new CreditCardTransaction(data)),
              ),
            )
            .catch((e: AxiosError) => {
              console.error(
                `Could not load activity history for order ${token}`,
                e?.response?.data
                  ? JSON.stringify(e?.response?.data, null, 2)
                  : JSON.stringify(e?.response, null, 2),
              );
            });
        }
      })
      .catch((e: AxiosError) => {
        toast.error('Não foi possível carregar a cobrança');
        console.error(
          'Não foi possível carregar a cobrança',
          e?.response?.data
            ? JSON.stringify(e?.response?.data, null, 2)
            : JSON.stringify(e?.response, null, 2),
        );
        onClose();
      })
      .finally(() => setLoading(false));
  }, [token, onClose]);

  const abortEditing = () => {
    setEditing(false);
    setInputDueDate(moment().format('YYYY-MM-DD'));
    setInputPriceState('');
    setInputMethod(1);
  };

  const startEditing = () => {
    setInputDueDate(order!.due_date!.format('YYYY-MM-DD'));
    setInputPriceState(order!.price!);
    setInputMethod(order!.bankId!);
    setEditing(true);
  };

  const setInputPriceState = (value: number | string) => {
    const v =
      typeof value === 'string'
        ? Number(String(value).replace(/\D/g, '')) / 100
        : value;
    setInputPrice(v.toFixed(2).replace('.', ','));
  };

  const submitForm = () => {
    if (!order?.id) {
      toast.error('ID da ordem não encontrado');
      return;
    }

    const payload: PatchOrderRequestData = {
      id: order.id,
      bank_id: inputMethod,
      due_date: inputDueDate,
      price: Number(inputPrice.replace(',', '.')),
    };

    Mixpanel.track('customers-financial-order-submitted-form');

    setLoading(true);
    patch(payload)
      .then(() => {
        loadOrder();
        abortEditing();
        loadCustomer();
        toast.success('Edição salva com sucesso');
      })
      .catch((e: AxiosError) => {
        toast.error('Não foi possivel salvar as alterações');
        console.error(
          'Não foi possivel salvar as alterações',
          e?.response?.data
            ? JSON.stringify(e?.response?.data, null, 2)
            : JSON.stringify(e?.response, null, 2),
        );
        setLoading(false);
      });
  };

  const processPayment = () => {
    confirm('Retentar cobrança', 'Deseja continuar?', () => {
      Mixpanel.track('customers-financial-order-clicked-try-pay');
      setLoading(true);
      if (!order?.token) {
        toast.error('Token não encontrado');
        setLoading(false);
        return;
      }
      payWithCard(order.token)
        .then(() => {
          setTimeout(() => {
            toast.success('Nova tentativa realizada');
            loadOrder();
          }, 2000);
        })
        .catch((e: AxiosError) => {
          toast.error('Não foi possível retentar o pagamento');
          console.error(
            'Não foi possível retentar o pagamento',
            e?.response?.data
              ? JSON.stringify(e?.response?.data, null, 2)
              : JSON.stringify(e?.response, null, 2),
          );
          setLoading(false);
        });
    });
  };

  const cancelOrder = () => {
    confirm('Cancelar cobrança', 'Deseja continuar?', () => {
      Mixpanel.track('customers-financial-order-clicked-cancel');
      setLoading(true);
      if (!order?.id) {
        toast.error('ID da ordem não encontrado');
        setLoading(false);
        return;
      }
      cancel(order.id)
        .then(() => {
          toast.success('Cobrança cancelada');
          loadOrder();
        })
        .catch((e: AxiosError) => {
          toast.error('Não foi possível cancelar a cobrança');
          console.error(
            'Não foi possível cancelar a cobrança',
            e?.response?.data
              ? JSON.stringify(e?.response?.data, null, 2)
              : JSON.stringify(e?.response, null, 2),
          );
          setLoading(false);
        });
    });
  };

  const generateOrderInvoice = () => {
    Mixpanel.track('customers-financial-order-clicked-generate-invoice');
    setLoading(true);
    if (!order?.token) {
      toast.error('Token não encontrado');
      setLoading(false);
      return;
    }
    generateInvoice(order.token)
      .then(() => {
        setTimeout(() => {
          toast.success('Boleto gerado');
          loadOrder();
        }, 3000);
      })
      .catch((e: AxiosError) => {
        toast.error('Falha ao gerar o boleto');
        console.error(
          'Falha ao gerar o boleto',
          e?.response?.data
            ? JSON.stringify(e?.response?.data, null, 2)
            : JSON.stringify(e?.response, null, 2),
        );
        setLoading(false);
      });
  };

  const downloadDanfse = async () => {
    if (!order?.nfse?.accessKey) return;
    try {
      setLoading(true);
      const { data } = await getDanfseNfse(order.nfse.accessKey);
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `danfse-${order.nfse.accessKey}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let errorMessage = 'Não foi possível baixar a DANFSE';
      const axiosError = e as AxiosError;

      if (axiosError?.response?.data instanceof Blob) {
        try {
          const text = await axiosError.response.data.text();
          const parsed = JSON.parse(text);

          if (parsed.message == 'Request failed with status code 502') {
            errorMessage =
              'Erro ao baixar a DANFSE, tente novamente em alguns minutos';
          } else if (parsed.status == 404) {
            errorMessage = 'DANFSE não encontrada';
          } else {
            errorMessage = parsed.message || errorMessage;
          }
        } catch {
          console.error(
            'Erro ao baixar DANFSE:',
            axiosError?.response?.status,
            errorMessage,
          );
        }
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const downloadXml = async () => {
    if (!order?.nfse?.accessKey) return;
    try {
      setLoading(true);
      const { data } = await getXmlNfse(order.nfse.accessKey);
      const blob = new Blob([data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nfse-${order.nfse.accessKey}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Não foi possível baixar o XML');
    } finally {
      setLoading(false);
    }
  };

  const sendEmail = async () => {
    if (!order?.nfse?.accessKey) return;
    try {
      setLoading(true);
      await sendEmailNfse(order.nfse.accessKey);
      toast.success('E-mail enviado com sucesso');
    } catch (e) {
      const axiosError = e as AxiosError;
      const statusCode = axiosError?.response?.status || 500;

      if (statusCode === 404) {
        toast.error('NFS-e não encontrada');
      } else if (statusCode === 400) {
        toast.error('Erro ao enviar o e-mail, verifique se o e-mail é válido');
      } else {
        toast.error(
          'Erro ao enviar o e-mail, tente novamente em alguns minutos',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const reverseOrder = () => {
    confirm('Estorno', 'Deseja continuar com o estorno?', () => {
      setLoading(true);
      if (!order?.id) {
        toast.error('ID da ordem não encontrado');
        setLoading(false);
        return;
      }
      reversalOrder(order.id)
        .then(() => {
          toast.success('Estorno realizado com sucesso');
          loadOrder();
        })
        .catch((e: AxiosError) => {
          toast.error('Não foi possível realizar o estorno');
          console.error(
            'Não foi possível realizar o estorno',
            e?.response?.data
              ? JSON.stringify(e?.response?.data, null, 2)
              : JSON.stringify(e?.response, null, 2),
          );
          setLoading(false);
        });
    });
  };

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const data: OrderModalContextData = {
    loading,
    setLoading,
    loadOrder,
    order,
    editing,
    startEditing,
    abortEditing,
    transactions,
    inputDueDate,
    setInputDueDate,
    inputPrice,
    setInputPriceState,
    inputMethod,
    setInputMethod,
    submitForm,
    processPayment,
    cancelOrder,
    generateOrderInvoice,
    downloadDanfse,
    downloadXml,
    sendEmail,
    reverseOrder,
  };

  return (
    <Context.Provider value={data}>
      <OrderModal isOpen={isOpen} onClose={onClose} />
    </Context.Provider>
  );
};

export default AddServicesContext;
